// WasteHunterAI.jsx - WITH AD ACCOUNT SELECTOR
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

// ============================================================
// UTILITIES
// ============================================================

const formatMoney = (amount, currency = 'USD') => {
  const num = parseFloat(amount) || 0;
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  } catch (error) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  }
};

const getSeverityColor = (severity) => {
  const colors = {
    critical: 'bg-red-500/10 border-red-500/30',
    high: 'bg-orange-500/10 border-orange-500/30',
    medium: 'bg-yellow-500/10 border-yellow-500/30',
    low: 'bg-blue-500/10 border-blue-500/30'
  };
  return colors[severity?.toLowerCase()] || colors.medium;
};

const getSeverityBadge = (severity) => {
  const colors = {
    critical: 'bg-red-500/20 text-red-400',
    high: 'bg-orange-500/20 text-orange-400',
    medium: 'bg-yellow-500/20 text-yellow-400',
    low: 'bg-blue-500/20 text-blue-400'
  };
  return colors[severity?.toLowerCase()] || colors.medium;
};

const getHealthScoreColor = (score) => {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-yellow-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
};

const getHealthScoreBg = (score) => {
  if (score >= 80) return 'bg-green-500/10 border-green-500/30';
  if (score >= 60) return 'bg-yellow-500/10 border-yellow-500/30';
  if (score >= 40) return 'bg-orange-500/10 border-orange-500/30';
  return 'bg-red-500/10 border-red-500/30';
};

const getHeatmapColor = (wastePercentage) => {
  if (wastePercentage > 60) return '#ef4444';
  if (wastePercentage > 40) return '#f97316';
  if (wastePercentage > 20) return '#eab308';
  if (wastePercentage > 10) return '#84cc16';
  return '#22c55e';
};

const getCampaignPersonality = (campaign) => {
  const waste = campaign.waste_percentage || 0;
  const conversions = campaign.conversions || 0;
  const spend = campaign.spend || 0;

  if (waste < 10) return { emoji: '⭐', name: 'The Champion', color: 'text-green-400' };
  if (conversions === 0 && spend > 500) return { emoji: '🧟', name: 'The Zombie', color: 'text-red-400' };
  if (waste > 60 && spend > 1000) return { emoji: '💣', name: 'The Time Bomb', color: 'text-red-400' };
  if (waste > 40 && conversions < 10) return { emoji: '🩸', name: 'The Bleeder', color: 'text-orange-400' };
  if (waste < 30) return { emoji: '🎯', name: 'The Performer', color: 'text-green-400' };
  return { emoji: '👀', name: 'The Watcher', color: 'text-gray-400' };
};

const paginate = (items, page, itemsPerPage) => {
  if (!items || items.length === 0) return [];
  const startIndex = (page - 1) * itemsPerPage;
  return items.slice(startIndex, startIndex + itemsPerPage);
};

// Get unique ad accounts from campaigns
const getAdAccounts = (campaigns) => {
  const accountMap = {};
  campaigns.forEach(campaign => {
    const accountId = campaign.account_id || campaign.ad_account_id || 'default';
    const accountName = campaign.account_name || campaign.ad_account_name || 'Ad Account';
    const currency = campaign.currency;
    
    if (!accountMap[accountId]) {
      accountMap[accountId] = {
        id: accountId,
        name: accountName,
        currency: currency,
        campaignCount: 0,
        totalWaste: 0,
        totalSpend: 0
      };
    }
    accountMap[accountId].campaignCount += 1;
    accountMap[accountId].totalWaste += parseFloat(campaign.waste_amount) || 0;
    accountMap[accountId].totalSpend += parseFloat(campaign.spend) || 0;
  });
  
  return Object.values(accountMap).sort((a, b) => b.totalWaste - a.totalWaste);
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const WasteHunterAI = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  
  const [detections, setDetections] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  
  const [selectedView, setSelectedView] = useState('overview');
  const [selectedAccount, setSelectedAccount] = useState('all');
  
  // Pagination
  const [overviewPage, setOverviewPage] = useState(1);
  const [detectionsPage, setDetectionsPage] = useState(1);
  const [predictionsPage, setPredictionsPage] = useState(1);
  const [heatmapPage, setHeatmapPage] = useState(1);
  const itemsPerPage = 6;

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://whydiditdie.com';

  useEffect(() => {
    checkAuth();
    loadWasteData();
  }, []);

  // Reset pagination when account changes
  useEffect(() => {
    setOverviewPage(1);
    setDetectionsPage(1);
    setPredictionsPage(1);
    setHeatmapPage(1);
  }, [selectedAccount]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login');
    }
  };

  const loadWasteData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }

      logger.info('📊 Loading waste hunter data...');

      const response = await fetch(`${BACKEND_URL}/api/waste-ai`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load waste data');
      }

      const data = await response.json();
      
      logger.info('✅ Waste data loaded:', {
        detections: data.detections?.length || 0,
        predictions: data.predictions?.length || 0,
        campaigns: data.campaigns?.length || 0
      });

      setDetections(data.detections || []);
      setPredictions(data.predictions || []);
      setSummary(data.summary || getEmptySummary());
      setCampaigns(data.campaigns || []);

    } catch (err) {
      logger.error('❌ Failed to load waste data:', err);
      setError('Failed to load waste intelligence data');
      setSummary(getEmptySummary());
    } finally {
      setLoading(false);
    }
  };

  const runScan = async () => {
    try {
      setScanning(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }

      logger.info('🚀 Triggering waste analysis...');

      const response = await fetch(`${BACKEND_URL}/api/waste-ai/scan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ force_rescan: true })
      });

      if (!response.ok) {
        throw new Error('Scan failed');
      }

      const result = await response.json();
      
      if (result.success) {
        logger.info('✅ Scan completed:', result.message);
        setError(`✅ ${result.message}`);
        await loadWasteData();
      } else {
        throw new Error(result.message || 'Scan failed');
      }

    } catch (err) {
      logger.error('❌ Scan failed:', err);
      setError(`Scan failed: ${err.message}`);
    } finally {
      setScanning(false);
    }
  };

  const getEmptySummary = () => ({
    total_detections: 0,
    total_predictions: 0,
    critical_alerts: 0,
    estimated_daily_waste: 0,
    estimated_hourly_burn_rate: 0,
    campaigns_analyzed: 0,
    primary_currency: 'USD',
    avg_confidence: 0,
    gemini_powered: false
  });

  // ============================================================
  // FILTERED DATA BASED ON SELECTED ACCOUNT
  // ============================================================
  
  const adAccounts = getAdAccounts(campaigns);
  
  const filteredCampaigns = selectedAccount === 'all'
    ? campaigns
    : campaigns.filter(c => (c.account_id || c.ad_account_id || 'default') === selectedAccount);
  
  const filteredCampaignIds = filteredCampaigns.map(c => c.campaign_id);
  
  const filteredDetections = selectedAccount === 'all'
    ? detections
    : detections.filter(d => filteredCampaignIds.includes(d.campaign_id));
  
  const filteredPredictions = selectedAccount === 'all'
    ? predictions
    : predictions.filter(p => filteredCampaignIds.includes(p.campaign_id));
  
  const selectedAccountData = adAccounts.find(a => a.id === selectedAccount);
  const selectedCurrency = selectedAccountData?.currency || summary?.primary_currency || 'USD';
  
  // Recalculate summary for filtered data
  const filteredSummary = selectedAccount === 'all' ? summary : {
    ...summary,
    total_detections: filteredDetections.length,
    total_predictions: filteredPredictions.length,
    critical_alerts: filteredDetections.filter(d => d.severity === 'critical').length,
    campaigns_analyzed: filteredCampaigns.length,
    estimated_daily_waste: filteredCampaigns.reduce((sum, c) => sum + (parseFloat(c.waste_amount) || 0), 0) / 30,
    estimated_hourly_burn_rate: filteredCampaigns.reduce((sum, c) => sum + (parseFloat(c.waste_amount) || 0), 0) / 30 / 24,
    primary_currency: selectedCurrency,
    avg_confidence: filteredDetections.length > 0
      ? Math.round(filteredDetections.reduce((sum, d) => sum + (d.confidence_score || 0), 0) / filteredDetections.length)
      : 0
  };

  // Check if viewing multi-currency (only when "all" is selected and multiple currencies exist)
  const isMultiCurrency = selectedAccount === 'all' && adAccounts.length > 1;

  // ============================================================
  // COMPUTE HEATMAP DATA
  // ============================================================
  const getHeatmapData = () => {
    return filteredCampaigns
      .map(campaign => ({
        campaign_id: campaign.campaign_id,
        campaign_name: campaign.name,
        waste_score: campaign.waste_percentage || 0,
        waste_amount: campaign.waste_amount || 0,
        spend: campaign.spend || 0,
        currency: campaign.currency,
        color: getHeatmapColor(campaign.waste_percentage || 0)
      }))
      .sort((a, b) => b.waste_score - a.waste_score);
  };

  // ============================================================
  // COMPUTE AUDIENCE ANALYSIS
  // ============================================================
  const getAudienceAnalysis = () => {
    return filteredCampaigns
      .filter(c => c.impressions > 0)
      .map(campaign => {
        const impressions = parseInt(campaign.impressions) || 0;
        const reach = parseInt(campaign.reach) || 0;
        
        let frequency = 0;
        
        if (campaign.frequency && !isNaN(parseFloat(campaign.frequency))) {
          frequency = parseFloat(campaign.frequency);
        } else if (reach > 0 && impressions > 0) {
          frequency = impressions / reach;
        } else if (impressions > 0) {
          frequency = 3.0;
        }
        
        frequency = Math.round(frequency * 10) / 10;
        
        let saturation = 0;
        let status = 'healthy';
        
        if (frequency > 0) {
          if (frequency > 7) {
            saturation = 100;
            status = 'critical';
          } else if (frequency > 5) {
            saturation = 80;
            status = 'warning';
          } else if (frequency > 3) {
            saturation = 50;
            status = 'caution';
          } else {
            saturation = frequency * 15;
            status = 'healthy';
          }
        } else {
          saturation = Math.min(100, (campaign.waste_percentage || 0) * 1.2);
          if (saturation > 70) status = 'warning';
          if (saturation > 85) status = 'critical';
        }
        
        const displayReach = reach > 0 ? reach : Math.floor(impressions / (frequency || 3));
        
        return {
          campaign_id: campaign.campaign_id,
          campaign_name: campaign.name,
          impressions,
          reach: displayReach,
          frequency: frequency || 3.0,
          saturation_percentage: Math.min(100, Math.round(saturation)),
          status,
          waste_percentage: campaign.waste_percentage || 0
        };
      })
      .sort((a, b) => b.saturation_percentage - a.saturation_percentage);
  };

  // ============================================================
  // COMPUTE 7-DAY FORECAST
  // ============================================================
  const getForecast = () => {
    const forecast = [];
    const dailyWaste = filteredSummary?.estimated_daily_waste || 0;
    const currency = selectedCurrency;
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(Date.now() + i * 24 * 60 * 60 * 1000);
      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      const dayPredictions = filteredPredictions.filter(p => 
        (p.days_until_critical || 0) === i
      );
      
      let predictedWaste = dailyWaste;
      let weatherType = 'clear';
      let severity = 'low';
      
      if (dayPredictions.length > 0) {
        const predictionWaste = dayPredictions.reduce((sum, p) => 
          sum + (p.estimated_waste || 0) / 7, 0
        );
        predictedWaste += predictionWaste;
      }
      
      const criticalCount = dayPredictions.filter(p => p.severity === 'critical').length;
      
      if (predictedWaste > dailyWaste * 2 || criticalCount > 2) {
        weatherType = 'storm';
        severity = 'critical';
      } else if (predictedWaste > dailyWaste * 1.5 || dayPredictions.length > 1) {
        weatherType = 'rain';
        severity = 'warning';
      } else if (predictedWaste > dailyWaste * 1.2) {
        weatherType = 'cloudy';
        severity = 'caution';
      }
      
      const icons = {
        clear: '☀️',
        cloudy: '☁️',
        rain: '🌧️',
        storm: '⛈️'
      };
      
      forecast.push({
        date: dateStr,
        day: dayOfWeek,
        weather_type: weatherType,
        severity: severity,
        predicted_waste: predictedWaste,
        icon: icons[weatherType],
        alerts: dayPredictions.length,
        currency: currency
      });
    }
    
    return forecast;
  };

  const heatmapData = getHeatmapData();
  const audienceData = getAudienceAnalysis();
  const forecastData = getForecast();

  const overviewTotalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);
  const detectionsTotalPages = Math.ceil(filteredDetections.length / itemsPerPage);
  const predictionsTotalPages = Math.ceil(filteredPredictions.length / itemsPerPage);
  const heatmapTotalPages = Math.ceil(heatmapData.length / (itemsPerPage * 4));

  // ============================================================
  // LOADING STATE
  // ============================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center p-4">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="text-6xl md:text-8xl animate-pulse">🔍</div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-24 h-24 md:w-32 md:h-32 border-4 border-purple-500/30 rounded-full animate-spin" 
                   style={{ borderTopColor: '#a855f7' }} />
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-red-400 bg-clip-text text-transparent">
            Loading Waste Intelligence...
          </h2>
        </div>
      </div>
    );
  }

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-7xl mx-auto p-4 space-y-6">

        {/* Header */}
        <div className="space-y-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white transition-colors text-sm md:text-base"
          >
            ← Back to Dashboard
          </button>

          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
            <div>
              <h1 className="text-3xl md:text-5xl font-black bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
                WASTE HUNTER
              </h1>
              <p className="text-gray-400 mt-2 text-sm md:text-base">
                {filteredSummary?.gemini_powered ? '✨ Powered by Aura AI' : 'Smart Analysis'} • {filteredSummary?.campaigns_analyzed || 0} campaigns analyzed
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Based on WhyDidItDie waste detection
              </p>
            </div>

            <button
              onClick={runScan}
              disabled={scanning}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg font-bold transition-all duration-200 transform hover:scale-105 text-sm md:text-base w-full md:w-auto"
            >
              {scanning ? '🔄 Analyzing...' : '🔍 Run Analysis'}
            </button>
          </div>
        </div>

        {/* Error/Success Message */}
        {error && (
          <div className={`p-4 rounded-lg text-sm md:text-base ${
            error.includes('✅') 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            {error}
          </div>
        )}

        {/* Ad Account Selector */}
        {adAccounts.length > 1 && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-gray-900/50 border border-gray-700 rounded-lg p-4">
            <label className="text-sm text-gray-400 whitespace-nowrap">📊 Viewing:</label>
            <select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              className="flex-1 bg-gray-800 border border-gray-600 text-white text-sm rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer appearance-none"
              style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
            >
              <option value="all">All Accounts ({campaigns.length} campaigns, {adAccounts.length} currencies)</option>
              {adAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.name} ({account.currency}) — {account.campaignCount} campaign{account.campaignCount !== 1 ? 's' : ''} — {formatMoney(account.totalWaste, account.currency)} waste
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Stats Banner */}
        <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-4 md:p-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-black text-purple-400">
                {filteredSummary?.total_detections || 0}
              </div>
              <div className="text-xs md:text-sm text-gray-400">Detections</div>
            </div>

            <div className="text-center">
              {isMultiCurrency ? (
                <>
                  <div className="text-xl md:text-2xl font-black text-yellow-400">
                    Multiple
                  </div>
                  <div className="text-xs md:text-sm text-gray-400">Select account ↑</div>
                </>
              ) : (
                <>
                  <div className="text-2xl md:text-3xl font-black text-red-400">
                    {formatMoney(filteredSummary?.estimated_daily_waste || 0, selectedCurrency)}
                  </div>
                  <div className="text-xs md:text-sm text-gray-400">Est. Daily Waste</div>
                </>
              )}
            </div>

            <div className="text-center">
              <div className="text-2xl md:text-3xl font-black text-orange-400">
                {filteredSummary?.critical_alerts || 0}
              </div>
              <div className="text-xs md:text-sm text-gray-400">Critical</div>
            </div>

            <div className="text-center">
              <div className="text-2xl md:text-3xl font-black text-yellow-400">
                {filteredSummary?.avg_confidence || 0}%
              </div>
              <div className="text-xs md:text-sm text-gray-400">Confidence</div>
            </div>

            <div className="text-center">
              <div className="text-lg md:text-xl font-bold text-green-400">
                {filteredSummary?.gemini_powered ? '✨ Aura' : 'Smart'}
              </div>
              <div className="text-xs md:text-sm text-gray-400">AI Mode</div>
            </div>
          </div>
        </div>

        {/* Burn Rate Display */}
        {!isMultiCurrency && filteredSummary && filteredSummary.estimated_daily_waste > 0 && (
          <div className="bg-gradient-to-br from-red-900/20 to-orange-900/20 border border-red-500/30 rounded-2xl p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              
              {/* Burn Rate Circle */}
              <div className="text-center">
                <h3 className="text-lg md:text-xl font-bold text-red-400 mb-4">💸 Current Burn Rate</h3>
                <div className="relative w-48 h-48 mx-auto">
                  <svg className="transform -rotate-90 w-48 h-48">
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-gray-700"
                    />
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${Math.min(550, (filteredSummary.estimated_hourly_burn_rate / 50) * 550)} 999`}
                      className="text-red-500"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-3xl md:text-4xl font-black text-white">
                      {formatMoney(filteredSummary.estimated_hourly_burn_rate || 0, selectedCurrency)}
                    </div>
                    <div className="text-sm text-gray-400">per hour</div>
                  </div>
                </div>
              </div>

              {/* Critical Alerts */}
              <div className="text-center">
                <h3 className="text-lg md:text-xl font-bold text-orange-400 mb-4">🚨 Critical Alerts</h3>
                <div className="space-y-3">
                  {filteredDetections
                    .filter(d => d.severity === 'critical')
                    .slice(0, 3)
                    .map((alert, index) => (
                      <div key={alert.id || index} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                        <div className="text-sm font-medium text-red-400 truncate">
                          {alert.campaign_name?.substring(0, 25)}...
                        </div>
                        <div className="text-xs text-gray-400 mt-1 truncate">
                          {alert.detection_title}
                        </div>
                      </div>
                    ))}
                  {filteredDetections.filter(d => d.severity === 'critical').length === 0 && (
                    <div className="text-gray-400 py-8 text-sm">
                      ✅ No critical alerts
                    </div>
                  )}
                </div>
              </div>

              {/* 24hr Stats */}
              <div className="text-center">
                <h3 className="text-lg md:text-xl font-bold text-yellow-400 mb-4">📊 Daily Stats</h3>
                <div className="space-y-4">
                  <div className="bg-black/30 rounded-lg p-4">
                    <div className="text-3xl font-black text-red-400">
                      {formatMoney(filteredSummary.estimated_daily_waste || 0, selectedCurrency)}
                    </div>
                    <div className="text-sm text-gray-400">Est. daily waste</div>
                  </div>
                  <div className="bg-black/30 rounded-lg p-4">
                    <div className="text-2xl font-bold text-orange-400">
                      {filteredPredictions.filter(p => (p.days_until_critical || 0) < 3).length}
                    </div>
                    <div className="text-sm text-gray-400">Urgent predictions</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Multi-Currency Notice */}
        {isMultiCurrency && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
            <div className="text-4xl mb-3">💱</div>
            <h3 className="text-lg font-bold text-yellow-400 mb-2">Multiple Currencies Detected</h3>
            <p className="text-gray-400 text-sm mb-4">
              You have campaigns in {adAccounts.length} different currencies. Select an ad account above to view accurate burn rates and forecasts.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {adAccounts.map(account => (
                <button
                  key={account.id}
                  onClick={() => setSelectedAccount(account.id)}
                  className="bg-gray-800 hover:bg-gray-700 border border-gray-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"
                >
                  {account.name} ({account.currency})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="overflow-x-auto">
          <div className="flex gap-2 bg-gray-900/50 p-1 rounded-lg min-w-max md:min-w-0">
            {['overview', 'detections', 'predictions', 'heatmap', 'audience', 'forecast'].map(view => (
              <button
                key={view}
                onClick={() => {
                  setSelectedView(view);
                  setOverviewPage(1);
                  setDetectionsPage(1);
                  setPredictionsPage(1);
                  setHeatmapPage(1);
                }}
                className={`px-3 md:px-4 py-2 rounded-md font-medium capitalize transition-all text-sm md:text-base whitespace-nowrap ${
                  selectedView === view
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {view === 'overview' && '📊'}
                {view === 'detections' && '🔍'}
                {view === 'predictions' && '🔮'}
                {view === 'heatmap' && '🔥'}
                {view === 'audience' && '👥'}
                {view === 'forecast' && '🌤️'}{' '}
                {view}
              </button>
            ))}
          </div>
        </div>

        {/* Campaign Health Overview */}
        {selectedView === 'overview' && (
          <div className="space-y-4">
            {filteredCampaigns.length === 0 ? (
              <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-8 text-center">
                <div className="text-6xl mb-4">✨</div>
                <h3 className="text-xl font-bold text-white mb-2">No Campaigns With Waste</h3>
                <p className="text-gray-400">
                  {selectedAccount === 'all' 
                    ? 'All campaigns are performing efficiently!' 
                    : 'This account has no campaigns with detected waste.'}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {paginate(filteredCampaigns, overviewPage, itemsPerPage).map((campaign) => {
                    const personality = getCampaignPersonality(campaign);
                    const healthScore = 100 - (campaign.waste_percentage || 0);
                    
                    return (
                      <div
                        key={campaign.campaign_id}
                        className={`border rounded-xl p-4 ${getHealthScoreBg(healthScore)}`}
                      >
                        <div className="mb-3">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h3 className="font-bold text-white text-sm line-clamp-2 flex-1">
                              {campaign.name}
                            </h3>
                            <span className={`text-xl ${personality.color}`}>
                              {personality.emoji}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400 mb-2">
                            {personality.name}
                          </div>
                          <div className={`text-4xl font-black ${getHealthScoreColor(healthScore)}`}>
                            {Math.round(healthScore)}%
                          </div>
                          <div className="text-xs text-gray-400">Health Score</div>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Waste:</span>
                            <span className="text-red-400 font-bold">
                              {formatMoney(campaign.waste_amount, campaign.currency)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Spend:</span>
                            <span className="text-white font-medium">
                              {formatMoney(campaign.spend, campaign.currency)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">CTR:</span>
                            <span className="text-white font-medium">
                              {((campaign.ctr || 0) * 100).toFixed(2)}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Conversions:</span>
                            <span className="text-white font-medium">
                              {Math.round(campaign.conversions || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {overviewTotalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-6">
                    <button
                      onClick={() => setOverviewPage(prev => Math.max(1, prev - 1))}
                      disabled={overviewPage === 1}
                      className="px-3 py-1 rounded bg-gray-800 text-white disabled:opacity-50 text-sm"
                    >
                      ←
                    </button>
                    <span className="text-sm text-gray-400">
                      Page {overviewPage} of {overviewTotalPages}
                    </span>
                    <button
                      onClick={() => setOverviewPage(prev => Math.min(overviewTotalPages, prev + 1))}
                      disabled={overviewPage === overviewTotalPages}
                      className="px-3 py-1 rounded bg-gray-800 text-white disabled:opacity-50 text-sm"
                    >
                      →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Detections View */}
        {selectedView === 'detections' && (
          <div className="space-y-4">
            {filteredDetections.length === 0 ? (
              <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-8 text-center">
                <div className="text-6xl mb-4">✨</div>
                <h3 className="text-xl font-bold text-white mb-2">No Issues Detected</h3>
                <p className="text-gray-400">
                  {selectedAccount === 'all' 
                    ? 'Your campaigns are performing well!' 
                    : 'This account has no detected issues.'}
                </p>
              </div>
            ) : (
              <>
                {paginate(filteredDetections, detectionsPage, itemsPerPage).map((detection) => (
                  <div
                    key={detection.id}
                    className={`border rounded-xl p-4 md:p-6 ${getSeverityColor(detection.severity)}`}
                  >
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-bold ${getSeverityBadge(detection.severity)}`}>
                            {detection.severity?.toUpperCase()}
                          </span>
                          <span className="text-xs md:text-sm text-gray-400">
                            {detection.detection_type?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                          {detection.gemini_enhanced && (
                            <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full">
                              ✨ AI Enhanced
                            </span>
                          )}
                          <span className="text-xs text-gray-500">
                            {detection.confidence_score}% confidence
                          </span>
                        </div>
                        
                        <h4 className="text-lg md:text-xl font-bold text-white mb-1">
                          {detection.detection_title}
                        </h4>
                        <p className="text-sm text-gray-400 mb-2">
                          {detection.campaign_name}
                        </p>
                        <p className="text-gray-300 text-sm md:text-base">
                          {detection.detection_description}
                        </p>

                        {detection.business_impact && (
                          <div className="mt-3 p-3 bg-black/30 rounded-lg">
                            <div className="text-sm font-semibold text-purple-400 mb-1">
                              💼 Business Impact
                            </div>
                            <p className="text-sm text-gray-300">{detection.business_impact}</p>
                          </div>
                        )}

                        {detection.root_cause && (
                          <div className="mt-3 p-3 bg-black/30 rounded-lg">
                            <div className="text-sm font-semibold text-orange-400 mb-1">
                              🎯 Root Cause
                            </div>
                            <p className="text-sm text-gray-300">{detection.root_cause}</p>
                          </div>
                        )}
                      </div>

                      <div className="text-center md:text-right">
                        <div className="text-2xl md:text-3xl font-black text-red-400">
                          {formatMoney(detection.estimated_daily_waste, detection.currency || selectedCurrency)}
                        </div>
                        <div className="text-xs md:text-sm text-gray-400">daily waste</div>
                      </div>
                    </div>

                    {/* Recommended Actions */}
                    {detection.recommended_actions && detection.recommended_actions.length > 0 && (
                      <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 md:p-4">
                        <div className="font-medium text-blue-400 mb-3 text-sm md:text-base">
                          🎯 Recommended Actions:
                        </div>
                        <div className="space-y-3">
                          {detection.recommended_actions.slice(0, 4).map((action, index) => (
                            <div key={index} className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 text-xs font-bold mt-0.5">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <div className="text-white font-medium text-sm md:text-base">
                                  {action.action}
                                </div>
                                {action.expected_impact && (
                                  <div className="text-xs text-gray-400 mt-1">
                                    Impact: {action.expected_impact}
                                  </div>
                                )}
                                {action.implementation_steps && action.implementation_steps.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {action.implementation_steps.map((step, stepIndex) => (
                                      <div key={stepIndex} className="text-xs text-gray-500">
                                        • {step}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Pagination */}
                {detectionsTotalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-6">
                    <button
                      onClick={() => setDetectionsPage(prev => Math.max(1, prev - 1))}
                      disabled={detectionsPage === 1}
                      className="px-3 py-1 rounded bg-gray-800 text-white disabled:opacity-50 text-sm"
                    >
                      ←
                    </button>
                    <span className="text-sm text-gray-400">
                      Page {detectionsPage} of {detectionsTotalPages}
                    </span>
                    <button
                      onClick={() => setDetectionsPage(prev => Math.min(detectionsTotalPages, prev + 1))}
                      disabled={detectionsPage === detectionsTotalPages}
                      className="px-3 py-1 rounded bg-gray-800 text-white disabled:opacity-50 text-sm"
                    >
                      →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Predictions View */}
        {selectedView === 'predictions' && (
          <div className="space-y-4">
            {filteredPredictions.length === 0 ? (
              <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-8 text-center">
                <div className="text-6xl mb-4">🔮</div>
                <h3 className="text-xl font-bold text-white mb-2">No Predictions</h3>
                <p className="text-gray-400">
                  {selectedAccount === 'all' 
                    ? 'No future risks detected' 
                    : 'No predictions for this account.'}
                </p>
              </div>
            ) : (
              <>
                {paginate(filteredPredictions, predictionsPage, itemsPerPage).map((prediction) => {
                  const campaign = filteredCampaigns.find(c => c.campaign_id === prediction.campaign_id);
                  const campaignName = campaign?.name || 'Unknown Campaign';
                  const predictionCurrency = prediction.currency || campaign?.currency || selectedCurrency;
                  
                  return (
                    <div
                      key={prediction.id}
                      className={`border rounded-xl p-4 md:p-6 ${getSeverityColor(prediction.severity)}`}
                    >
                      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-bold ${getSeverityBadge(prediction.severity)}`}>
                              {prediction.severity?.toUpperCase()}
                            </span>
                            <span className="text-xs md:text-sm text-gray-400">
                              {prediction.prediction_type?.replace(/_/g, ' ').toUpperCase()}
                            </span>
                          </div>
                          
                          <h4 className="text-lg md:text-xl font-bold text-white mb-1">
                            {prediction.prediction_title}
                          </h4>
                          
                          <p className="text-sm text-purple-400 mb-2">
                            📍 {campaignName}
                          </p>
                          
                          <p className="text-gray-300 text-sm md:text-base mb-3">
                            {prediction.prediction_description}
                          </p>

                          {prediction.gemini_reasoning && 
                           prediction.gemini_reasoning !== prediction.prediction_description && (
                            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mb-3">
                              <div className="text-xs font-semibold text-purple-400 mb-1">
                                ✨ AI Analysis
                              </div>
                              <p className="text-sm text-gray-300">{prediction.gemini_reasoning}</p>
                            </div>
                          )}

                          {prediction.recommended_action && (
                            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                              <div className="text-xs font-semibold text-blue-400 mb-1">
                                🎯 Recommended Action
                              </div>
                              <p className="text-sm text-white">{prediction.recommended_action}</p>
                            </div>
                          )}
                        </div>

                        <div className="text-center md:text-right">
                          <div className="text-3xl md:text-4xl font-black text-yellow-400">
                            {prediction.days_until_critical || 0}
                          </div>
                          <div className="text-xs md:text-sm text-gray-400">days until critical</div>
                          
                          {prediction.estimated_waste && (
                            <>
                              <div className="text-lg md:text-xl font-bold text-red-400 mt-3">
                                {formatMoney(prediction.estimated_waste, predictionCurrency)}
                              </div>
                              <div className="text-xs text-gray-500">potential waste</div>
                            </>
                          )}
                          
                          {prediction.confidence && (
                            <div className="text-sm text-gray-400 mt-2">
                              {prediction.confidence}% confident
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Pagination */}
                {predictionsTotalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-6">
                    <button
                      onClick={() => setPredictionsPage(prev => Math.max(1, prev - 1))}
                      disabled={predictionsPage === 1}
                      className="px-3 py-1 rounded bg-gray-800 text-white disabled:opacity-50 text-sm"
                    >
                      ←
                    </button>
                    <span className="text-sm text-gray-400">
                      Page {predictionsPage} of {predictionsTotalPages}
                    </span>
                    <button
                      onClick={() => setPredictionsPage(prev => Math.min(predictionsTotalPages, prev + 1))}
                      disabled={predictionsPage === predictionsTotalPages}
                      className="px-3 py-1 rounded bg-gray-800 text-white disabled:opacity-50 text-sm"
                    >
                      →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Heatmap View */}
        {selectedView === 'heatmap' && (
          <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 md:p-6">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-6">🔥 Waste Heatmap</h3>

            {heatmapData.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm md:text-base">
                {selectedAccount === 'all' 
                  ? 'No campaigns to display in heatmap' 
                  : 'No campaigns with waste in this account'}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                  {paginate(heatmapData, heatmapPage, itemsPerPage * 4).map(campaign => (
                    <div
                      key={campaign.campaign_id}
                      className="relative group cursor-pointer"
                      title={`${campaign.campaign_name}: ${campaign.waste_score.toFixed(1)}% waste`}
                    >
                      <div
                        className="aspect-square rounded-lg flex items-center justify-center text-white font-bold transition-all duration-200 hover:scale-110 hover:z-10"
                        style={{ backgroundColor: campaign.color }}
                      >
                        <span className="text-xs">
                          {Math.round(campaign.waste_score)}%
                        </span>
                      </div>

                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-20">
                        <div className="bg-gray-800 text-white text-xs rounded-lg p-2 whitespace-nowrap shadow-xl border border-gray-700">
                          <div className="font-bold">{campaign.campaign_name}</div>
                          <div>Waste: {Math.round(campaign.waste_score)}% ({formatMoney(campaign.waste_amount, campaign.currency)})</div>
                          <div>Spend: {formatMoney(campaign.spend, campaign.currency)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {heatmapTotalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-6">
                    <button
                      onClick={() => setHeatmapPage(prev => Math.max(1, prev - 1))}
                      disabled={heatmapPage === 1}
                      className="px-3 py-1 rounded bg-gray-800 text-white disabled:opacity-50 text-sm"
                    >
                      ←
                    </button>
                    <span className="text-sm text-gray-400">
                      Page {heatmapPage} of {heatmapTotalPages}
                    </span>
                    <button
                      onClick={() => setHeatmapPage(prev => Math.min(heatmapTotalPages, prev + 1))}
                      disabled={heatmapPage === heatmapTotalPages}
                      className="px-3 py-1 rounded bg-gray-800 text-white disabled:opacity-50 text-sm"
                    >
                      →
                    </button>
                  </div>
                )}

                <div className="mt-6 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-500"></div>
                    <span className="text-xs md:text-sm text-gray-400">Healthy (0-20%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-yellow-500"></div>
                    <span className="text-xs md:text-sm text-gray-400">Warning (20-60%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-500"></div>
                    <span className="text-xs md:text-sm text-gray-400">Critical (60-100%)</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Audience View */}
        {selectedView === 'audience' && (
          <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 md:p-6">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-6">👥 Audience Analysis</h3>

            {audienceData.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm md:text-base">
                {selectedAccount === 'all' 
                  ? 'No audience data available' 
                  : 'No audience data for this account'}
              </div>
            ) : (
              <div className="space-y-4">
                {audienceData.slice(0, 10).map(campaign => (
                  <div key={campaign.campaign_id} className="space-y-3">
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex-1">
                        <div className="font-medium text-white text-sm md:text-base truncate">
                          {campaign.campaign_name}
                        </div>
                        <div className="text-xs md:text-sm text-gray-400">
                          {campaign.impressions.toLocaleString()} impressions
                          {campaign.reach > 0 && ` • ${campaign.reach.toLocaleString()} reach`}
                          {campaign.frequency > 0 && ` • ${campaign.frequency.toFixed(1)}x frequency`}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className={`text-xl md:text-2xl font-bold ${
                          campaign.status === 'critical' ? 'text-red-400' :
                          campaign.status === 'warning' ? 'text-orange-400' :
                          'text-green-400'
                        }`}>
                          {Math.round(campaign.saturation_percentage)}%
                        </div>
                        <div className="text-xs md:text-sm text-gray-400 capitalize">
                          {campaign.status}
                        </div>
                      </div>
                    </div>

                    <div className="w-full bg-gray-800 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${
                          campaign.saturation_percentage > 80 ? 'bg-red-500' :
                          campaign.saturation_percentage > 50 ? 'bg-orange-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(100, campaign.saturation_percentage)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Forecast View */}
        {selectedView === 'forecast' && (
          <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 md:p-6">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-6">🌤️ 7-Day Waste Forecast</h3>

            {isMultiCurrency ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-3">💱</div>
                <h4 className="text-lg font-bold text-yellow-400 mb-2">Select an Ad Account</h4>
                <p className="text-gray-400 text-sm mb-4">
                  Choose an account above to view accurate forecast data in a single currency.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-7 gap-1 md:gap-2">
                  {forecastData.map((day, index) => (
                    <div
                      key={index}
                      className={`text-center p-2 md:p-4 rounded-lg border ${
                        day.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                        day.severity === 'warning' ? 'bg-orange-500/10 border-orange-500/30' :
                        day.severity === 'caution' ? 'bg-yellow-500/10 border-yellow-500/30' :
                        'bg-green-500/10 border-green-500/30'
                      }`}
                    >
                      <div className="text-xs md:text-sm font-medium text-gray-300 mb-2">
                        {day.day}
                      </div>
                      <div className="text-2xl md:text-4xl mb-2">{day.icon}</div>
                      <div className="text-xs text-gray-400 mb-2 hidden md:block">
                        {day.date}
                      </div>
                      <div className={`text-xs md:text-sm font-bold ${
                        day.severity === 'critical' ? 'text-red-400' :
                        day.severity === 'warning' ? 'text-orange-400' :
                        day.severity === 'caution' ? 'text-yellow-400' :
                        'text-green-400'
                      }`}>
                        {formatMoney(day.predicted_waste, day.currency)}
                      </div>
                      {day.alerts > 0 && (
                        <div className="text-xs text-orange-400 mt-1">
                          {day.alerts} 🚨
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  <div className="bg-black/30 rounded-lg p-3 md:p-4 text-center">
                    <span className="text-2xl md:text-3xl">☀️</span>
                    <div className="text-xs md:text-sm text-gray-400 mt-2">Clear</div>
                    <div className="text-xs text-gray-500 hidden md:block">Minimal waste</div>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 md:p-4 text-center">
                    <span className="text-2xl md:text-3xl">☁️</span>
                    <div className="text-xs md:text-sm text-gray-400 mt-2">Cloudy</div>
                    <div className="text-xs text-gray-500 hidden md:block">Minor waste</div>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 md:p-4 text-center">
                    <span className="text-2xl md:text-3xl">🌧️</span>
                    <div className="text-xs md:text-sm text-gray-400 mt-2">Rain</div>
                    <div className="text-xs text-gray-500 hidden md:block">Significant waste</div>
                  </div>
<div className="bg-black/30 rounded-lg p-3 md:p-4 text-center">
                    <span className="text-2xl md:text-3xl">⛈️</span>
                    <div className="text-xs md:text-sm text-gray-400 mt-2">Storm</div>
                    <div className="text-xs text-gray-500 hidden md:block">Critical waste</div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default WasteHunterAI;